import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronRight, Loader2, X } from 'lucide-react';
import { useExercises } from '@/hooks/useWorkout';
import { useTelegram } from '@/hooks/useTelegram';
import { MUSCLE_GROUP_META, type MuscleGroup, type Exercise } from '@/types/workout';

// Группы мышц для сетки (карта)
const MUSCLE_GROUPS: MuscleGroup[] = ['legs', 'back', 'chest', 'shoulders', 'arms', 'core', 'full_body'];

const TARGET_RU: Record<string, string> = {
  abs: 'Пресс', abductors: 'Отводящие', adductors: 'Приводящие',
  biceps: 'Бицепс', calves: 'Икры', cardiovascular: 'Кардио',
  chest: 'Грудь', delts: 'Дельты', forearms: 'Предплечья',
  glutes: 'Ягодицы', hamstrings: 'Бицепс бедра', hip: 'Бёдра',
  'hip flexors': 'Сгибатели бедра',
  'it band': 'Илиотибиальный тракт',
  lats: 'Широчайшие', 'levator scapulae': 'Леватор лопатки',
  'lower back': 'Поясница', 'lower body': 'Низ тела',
  neck: 'Шея', obliques: 'Косые мышцы', pectorals: 'Грудные',
  quads: 'Квадрицепс', serratus: 'Зубчатые',
  'serratus anterior': 'Передние зубчатые',
  shoulders: 'Плечи', spine: 'Позвоночник',
  traps: 'Трапеции', triceps: 'Трицепс',
  'upper back': 'Верх спины', 'wrist flexors': 'Сгибатели запястья',
  // дополнительные из датасета
  'anterior deltoid': 'Передняя дельта',
  'posterior deltoid': 'Задняя дельта',
  'medial deltoid': 'Средняя дельта',
  'lateral deltoid': 'Боковая дельта',
  'middle back': 'Средняя спина',
  'inner thighs': 'Внутренняя поверхность бедра',
  'outer thighs': 'Внешняя поверхность бедра',
  'hip abductors': 'Отводящие бедра',
  'hip adductors': 'Приводящие бедра',
  'rectus abdominis': 'Прямая мышца живота',
  'transverse abdominis': 'Поперечная мышца живота',
  'erector spinae': 'Разгибатели позвоночника',
  rhomboids: 'Ромбовидные', brachialis: 'Плечевая', brachioradialis: 'Плечелучевая',
  'tibialis anterior': 'Передняя большеберцовая',
  'soleus': 'Камбаловидная', gastrocnemius: 'Икроножная',
  'iliopsoas': 'Подвздошно-поясничная',
  'rectus femoris': 'Прямая мышца бедра',
  'vastus lateralis': 'Латеральная широкая', 'vastus medialis': 'Медиальная широкая',
  'biceps femoris': 'Двуглавая бедра', 'gluteus maximus': 'Большая ягодичная',
  'gluteus medius': 'Средняя ягодичная', 'gluteus minimus': 'Малая ягодичная',
  'tensor fasciae latae': 'Напрягатель широкой фасции',
  infraspinatus: 'Подостная', supraspinatus: 'Надостная',
  subscapularis: 'Подлопаточная', teres: 'Малая круглая',
  'teres major': 'Большая круглая', 'teres minor': 'Малая круглая',
  'long head biceps': 'Длинная головка бицепса',
  'short head biceps': 'Короткая головка бицепса',
  'long head triceps': 'Длинная головка трицепса',
  'lateral head triceps': 'Боковая головка трицепса',
  'medial head triceps': 'Медиальная головка трицепса',
  core: 'Кор', back: 'Спина', legs: 'Ноги', arms: 'Руки',
  'full body': 'Всё тело', 'upper body': 'Верх тела',
};

const EQUIP_RU: Record<string, string> = {
  'body weight': 'Без оборудования', band: 'Эспандер', barbell: 'Штанга',
  'bosu ball': 'BOSU', cable: 'Кабельный тренажёр',
  dumbbell: 'Гантели', 'ez barbell': 'EZ-штанга',
  hammer: 'Хаммер', kettlebell: 'Гиря',
  'leverage machine': 'Тренажёр', 'medicine ball': 'Медбол',
  'resistance band': 'Резиновая лента', roller: 'Ролик',
  rope: 'Канат', 'smith machine': 'Машина Смита',
  'sled machine': 'Сани', 'stability ball': 'Фитбол',
  tire: 'Покрышка', 'trap bar': 'Трэп-гриф',
  'upper body ergometer': 'Ручной эргометр',
  weighted: 'С отягощением', 'wheel roller': 'Колёсный ролик',
};

function tTarget(v?: string | null) {
  if (!v) return '';
  return TARGET_RU[v.toLowerCase()] ?? v;
}
function tEquip(v?: string | null) {
  if (!v) return '';
  return EQUIP_RU[v.toLowerCase()] ?? v;
}

export function ExerciseLibrary() {
  const { haptic } = useTelegram();
  const [selectedGroup, setSelectedGroup] = useState<MuscleGroup | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);

  const { exercises, loading } = useExercises(selectedGroup ?? undefined);

  const filtered = searchQuery.trim()
    ? exercises.filter((ex) =>
        ex.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ex.target?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : exercises;

  function selectGroup(g: MuscleGroup) {
    haptic('light');
    setSelectedGroup(g);
    setSearchQuery('');
  }

  function back() {
    haptic('light');
    if (selectedExercise) { setSelectedExercise(null); return; }
    setSelectedGroup(null);
    setSearchQuery('');
  }

  // Экран детали упражнения
  if (selectedExercise) {
    return <ExerciseDetail exercise={selectedExercise} onBack={back} />;
  }

  return (
    <div className="py-4 space-y-4">
      {/* Поиск */}
      <div className="px-4">
        <div className="flex items-center gap-2 bg-bg-elevated border border-line rounded-xl px-3 py-2">
          <Search size={14} className="text-ink-dim shrink-0" />
          <input
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (e.target.value && !selectedGroup) setSelectedGroup(null);
            }}
            placeholder="Найти упражнение..."
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-dim"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}>
              <X size={14} className="text-ink-dim" />
            </button>
          )}
        </div>
      </div>

      {/* Сетка групп мышц (если не выбрана группа и нет поиска) */}
      {!selectedGroup && !searchQuery && (
        <div className="px-4">
          <div className="text-[10px] text-ink-dim uppercase tracking-wider font-semibold mb-3 px-1">
            Выбери группу мышц
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {MUSCLE_GROUPS.map((group, i) => {
              const meta = MUSCLE_GROUP_META[group];
              return (
                <motion.button
                  key={group}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => selectGroup(group)}
                  className={`flex items-center gap-3 p-3.5 rounded-2xl bg-gradient-to-br ${meta.color} border border-white/5 active:scale-[0.97] transition-all`}
                >
                  <span className="text-2xl">{meta.emoji}</span>
                  <div className="text-left">
                    <div className="text-sm font-semibold">{meta.label}</div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      )}

      {/* Список упражнений */}
      {(selectedGroup || searchQuery) && (
        <div className="px-4 space-y-3">
          {/* Хлебная крошка */}
          {selectedGroup && (
            <div className="flex items-center gap-2">
              <button
                onClick={back}
                className="text-[11px] text-emerald-glow font-medium"
              >
                ← Все группы
              </button>
              <span className="text-ink-dim text-[11px]">/</span>
              <span className="text-[11px] text-ink-muted">
                {MUSCLE_GROUP_META[selectedGroup].emoji} {MUSCLE_GROUP_META[selectedGroup].label}
              </span>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-emerald-glow" />
            </div>
          ) : (
            <>
              <div className="text-[10px] text-ink-dim px-1">
                {filtered.length} упражнений
              </div>
              <div className="space-y-2">
                {filtered.map((ex, i) => (
                  <motion.button
                    key={ex.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.3) }}
                    onClick={() => { haptic('light'); setSelectedExercise(ex); }}
                    className="w-full bg-bg-card border border-line rounded-xl p-3 flex items-center gap-3 active:scale-[0.99] transition-all"
                  >
                    {/* GIF превью */}
                    {ex.gif_url ? (
                      <img
                        src={ex.gif_url}
                        alt={ex.name}
                        className="w-12 h-12 rounded-lg object-cover shrink-0 bg-bg-elevated"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-bg-elevated flex items-center justify-center shrink-0">
                        <span className="text-xl">💪</span>
                      </div>
                    )}
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-sm font-medium truncate">{ex.name}</div>
                      <div className="text-[11px] text-ink-muted truncate mt-0.5">
                        {ex.target && <span>{tTarget(ex.target)}</span>}
                        {ex.equipment && <span className="text-ink-dim"> · {tEquip(ex.equipment)}</span>}
                      </div>
                    </div>
                    <ChevronRight size={15} className="text-ink-dim shrink-0" />
                  </motion.button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Детальная карточка упражнения ───────────────────────────────────────────

function ExerciseDetail({ exercise, onBack }: { exercise: Exercise; onBack: () => void }) {
  const [gifLoaded, setGifLoaded] = useState(false);
  const [showFilmingTip, setShowFilmingTip] = useState(false);
  const { haptic } = useTelegram();

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="pb-8"
    >
      {/* Назад */}
      <div className="px-4 pt-4 pb-2">
        <button onClick={onBack} className="text-[11px] text-emerald-glow font-medium">
          ← Назад
        </button>
      </div>

      {/* GIF */}
      <div className="relative mx-4 rounded-2xl overflow-hidden bg-bg-elevated aspect-video mb-4">
        {!gifLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-glow" />
          </div>
        )}
        {exercise.gif_url && (
          <img
            src={exercise.gif_url}
            alt={exercise.name}
            onLoad={() => setGifLoaded(true)}
            className={`w-full h-full object-contain transition-opacity ${gifLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
      </div>

      <div className="px-4 space-y-4">
        {/* Название и теги */}
        <div>
          <h2 className="font-display font-bold text-lg">{exercise.name}</h2>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {exercise.target && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-glow/15 text-emerald-glow text-[10px] font-medium">
                {tTarget(exercise.target)}
              </span>
            )}
            {exercise.equipment && (
              <span className="px-2 py-0.5 rounded-full bg-bg-elevated border border-line text-ink-dim text-[10px]">
                {tEquip(exercise.equipment)}
              </span>
            )}
            {exercise.secondary_muscles?.map((m) => (
              <span key={m} className="px-2 py-0.5 rounded-full bg-bg-elevated border border-line text-ink-dim text-[10px]">
                {tTarget(m)}
              </span>
            ))}
          </div>
        </div>

        {/* Техника выполнения — русский приоритет */}
        {(exercise.instructions_ru || exercise.instructions_en) && (
          <div className="bg-bg-card border border-line rounded-xl p-3 space-y-2">
            <div className="text-[10px] text-ink-dim uppercase tracking-wider font-semibold">
              Техника выполнения
            </div>
            <ol className="space-y-1.5">
              {(exercise.instructions_ru || exercise.instructions_en)!
                .split(/\.\s+/)
                .filter(Boolean)
                .map((step, i) => (
                  <li key={i} className="flex gap-2 text-[12px] text-ink-muted leading-relaxed">
                    <span className="shrink-0 w-4 h-4 rounded-full bg-emerald-glow/20 text-emerald-glow text-[10px] flex items-center justify-center mt-0.5 font-bold">
                      {i + 1}
                    </span>
                    <span>{step.trim().replace(/\.$/, '')}</span>
                  </li>
                ))}
            </ol>
          </div>
        )}

        {/* Совет по съёмке техники */}
        <button
          onClick={() => { haptic('light'); setShowFilmingTip(!showFilmingTip); }}
          className="w-full bg-bg-card border border-line rounded-xl p-3 flex items-center justify-between active:bg-bg-elevated transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="text-base">📸</span>
            <span className="text-[12px] font-medium">Как снимать себя для контроля техники</span>
          </div>
          <ChevronRight
            size={14}
            className={`text-ink-dim transition-transform ${showFilmingTip ? 'rotate-90' : ''}`}
          />
        </button>

        <AnimatePresence>
          {showFilmingTip && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="bg-bg-elevated border border-line rounded-xl p-3 space-y-2 text-[11px] text-ink-muted leading-relaxed">
                <p>Съёмка техники ускоряет прогресс и помогает избежать травм.</p>
                <div className="space-y-1.5 mt-2">
                  <p>📐 <strong>Сбоку</strong> — виден нейтральный позвоночник и глубина движения</p>
                  <p>👁 <strong>Спереди</strong> — симметрия, положение коленей и плеч</p>
                  <p>🔙 <strong>Сзади</strong> — положение таза и спины</p>
                </div>
                <p className="mt-2 text-ink-dim">Используй штатив или прислони телефон. Видео можно отправить тренеру для обратной связи.</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Дисклеймер */}
        <p className="text-[10px] text-ink-dim leading-relaxed">
          ⚠️ Информация носит ознакомительный характер. Для максимальной безопасности и эффективности проконсультируйся с сертифицированным тренером.
        </p>
      </div>
    </motion.div>
  );
}
